// C:\ViYash\XPRSuvey\XPRSuvey-App\src\Components\Survey\Pages\Dashboard.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ClipboardList,
  Building2,
  Users,
  User,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../Utility/userContext";
import { GetDashboardData, GetGraphDataByFilter } from "../Services/SurveyService";

const SPARKLINE_SHAPES = {
  assessments: [40, 55, 50, 62, 58, 70, 66, 78, 72, 85],
  companies: [50, 48, 60, 55, 65, 60, 70, 66, 74, 70],
  hrUsers: [45, 50, 48, 55, 52, 58, 55, 60, 57, 62],
  responses: [35, 45, 40, 55, 60, 52, 68, 62, 75, 80],
};

// --- Month/year filter constants (Assessment overview by channel) ---
const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Adjust `span` if you need to filter further back than 5 years.
const getYearOptions = (span = 5) => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: span }, (_, i) => currentYear - i);
};

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const formatDate = (isoString) => {
  if (!isoString) return "-";
  let d = new Date(isoString);

  if (Number.isNaN(d.getTime())) {
    const parts = isoString.split(" ")[0].split("-");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      d = new Date(`${year}-${month}-${day}`);
    }
  }

  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const StatCard = ({ card, rows = [], isHovered, onMouseEnter, onMouseLeave, navigate }) => {
  const Icon = card.icon;

  const cardBody = (extended) => (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${card.iconBg} ${card.iconColor}`}>
            <Icon size={18} />
          </span>
          <span className="truncate text-sm font-medium text-gray-500">{card.label}</span>
        </div>

        <ChevronDown
          className="h-4 w-4 shrink-0 cursor-pointer text-gray-300"
          strokeWidth={2}
          onMouseEnter={onMouseEnter}
        />
      </div>

      <div className="mt-2 text-xl font-bold text-gray-900">{card.value}</div>

      {extended && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
            {rows.map((row) => {
              const isStackedLabel = typeof row.label === "object" && row.label !== null;
              const rowKey = isStackedLabel ? `${row.label.title}-${row.label.subtitle}` : row.label;
              return (
                <div key={rowKey} className="flex items-center justify-between text-sm">
                  {isStackedLabel ? (
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">{row.label.title}</p>
                      <p className="truncate text-xs text-gray-400">{row.label.subtitle}</p>
                    </div>
                  ) : (
                    <span className="text-gray-500">{row.label}</span>
                  )}
                  <span className="ml-3 shrink-0 font-semibold text-gray-900">{row.value}</span>
                </div>
              );
            })}
          </div>

          {card.viewAllPath && (
            <button
              type="button"
              onClick={() => navigate(card.viewAllPath)}
              className="mt-3 flex w-full items-center justify-end gap-1 text-xs font-semibold text-[#7f35b2] hover:underline"
            >
              View all
              <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="relative">
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        {cardBody(false)}
      </div>

      {isHovered && (
        <div
          className="absolute left-0 top-0 w-full rounded-2xl border border-gray-100 bg-white p-4 shadow-xl"
          style={{ zIndex: 50 }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {cardBody(true)}
        </div>
      )}
    </div>
  );
};

// --- Month/year filter control for the Assessment overview by channel chart ---
const MonthYearFilter = ({ month, year, years, onMonthChange, onYearChange, disabled }) => (
  <div className="flex items-center gap-2">
    <div className="relative">
      <select
        value={month}
        disabled={disabled}
        onChange={(e) => onMonthChange(Number(e.target.value))}
        aria-label="Filter month"
        className="appearance-none rounded-md border border-gray-200 bg-white py-1.5 pl-3 pr-7 text-xs font-semibold text-gray-700 focus:border-[#7f35b2] focus:outline-none focus:ring-1 focus:ring-[#7f35b2] disabled:opacity-50"
      >
        {MONTHS.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
    </div>

    <div className="relative">
      <select
        value={year}
        disabled={disabled}
        onChange={(e) => onYearChange(Number(e.target.value))}
        aria-label="Filter year"
        className="appearance-none rounded-md border border-gray-200 bg-white py-1.5 pl-3 pr-7 text-xs font-semibold text-gray-700 focus:border-[#7f35b2] focus:outline-none focus:ring-1 focus:ring-[#7f35b2] disabled:opacity-50"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
    </div>
  </div>
);

const useElementSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const apply = (w, h) =>
      setSize((prev) =>
        Math.abs(prev.width - w) < 1 && Math.abs(prev.height - h) < 1
          ? prev
          : { width: w, height: h }
      );

    apply(el.clientWidth, el.clientHeight);

    if (typeof ResizeObserver === "undefined") {
      const onResize = () => apply(el.clientWidth, el.clientHeight);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) apply(box.width, box.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
};

const niceScale = (maxValue, tickCount = 4) => {
  if (!maxValue || maxValue <= 0) return { max: 4, ticks: [0, 1, 2, 3, 4] };

  const rough = maxValue / tickCount;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  const stepMultiplier = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  const step = stepMultiplier * pow;
  const max = Math.ceil(maxValue / step) * step;

  const ticks = [];
  for (let v = 0; v <= max + step / 1000; v += step) {
    ticks.push(Math.round(v * 1000) / 1000);
  }
  return { max, ticks };
};

const truncate = (text, maxChars) => {
  const value = String(text ?? "");
  if (maxChars <= 1) return "";
  return value.length > maxChars ? `${value.slice(0, maxChars - 1)}…` : value;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const CHART_SERIES = [
  { key: "created", label: "Created", color: "#B39DDB", hoverColor: "#7f35b2", },
  { key: "completed", label: "Completed", color: "#81D4B5", hoverColor: "#10b981", },
  { key: "responses", label: "Responses", color: "#FFB88C", hoverColor: "#f97316", },
  { key: "responsesCompleted", label: "Responses Completed", color: "#8BB8F5", hoverColor: "#3b82f6", },
];

const topRoundedRectPath = (x, y, w, h, r) => {
  if (h <= 0.5 || w <= 0) return "";
  const radius = Math.min(r, w / 2, h);
  if (radius <= 0.5) {
    return `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`;
  }
  return `M ${x} ${y + radius}
    A ${radius} ${radius} 0 0 1 ${x + radius} ${y}
    H ${x + w - radius}
    A ${radius} ${radius} 0 0 1 ${x + w} ${y + radius}
    V ${y + h}
    H ${x}
    Z`;
};

const OverviewChart = ({ rawData }) => {
  const data = Array.isArray(rawData) ? rawData : [];
  const seriesCount = CHART_SERIES.length;
  const [wrapRef, { width, height }] = useElementSize();
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const geo = useMemo(() => {
    if (!width || !height || data.length === 0) return null;

    const compact = width < 480;

    const maxValue = data.reduce(
      (acc, row) =>
        Math.max(acc, ...CHART_SERIES.map((s) => Number(row[s.key]) || 0)),
      0
    );
    const { max: yMax, ticks } = niceScale(maxValue, compact ? 3 : 4);

    const padding = {
      top: 5,      // Reduced from 14 to free up top space
      right: compact ? 8 : 14,
      bottom: 33,  // Reduced from 36/42 to gain extra vertical plot area
      left: 14 + String(ticks[ticks.length - 1]).length * 7,
    };

    const plotW = Math.max(width - padding.left - padding.right, 10);
    const plotH = Math.max(height - padding.top - padding.bottom, 10);

    const groupWidth = plotW / data.length;
    const groupInset = clamp(groupWidth * 0.12, 2, 16);
    const innerWidth = Math.max(groupWidth - groupInset * 2, 6);
    const barGap = clamp(innerWidth * 0.08, 1, 6);
    const barWidth = Math.max((innerWidth - barGap * (seriesCount - 1)) / seriesCount, 2);
    const barRadius = Math.min(3, barWidth / 2);

    const baseline = padding.top + plotH;
    const yAt = (v) => baseline - ((Number(v) || 0) / yMax) * plotH;
    const barHeight = (v) => ((Number(v) || 0) / yMax) * plotH;

    const groupX = (i) => padding.left + i * groupWidth;
    const groupCenter = (i) => groupX(i) + groupWidth / 2;
    const barX = (i, si) => groupX(i) + groupInset + si * (barWidth + barGap);

    const maxChars = Math.max(4, Math.floor(groupWidth / 6.5));
    const labelEvery = groupWidth > 0 && groupWidth < 46 ? Math.ceil(46 / groupWidth) : 1;

    return {
      padding,
      plotW,
      plotH,
      yMax,
      ticks,
      baseline,
      yAt,
      barHeight,
      groupWidth,
      groupX,
      groupCenter,
      barX,
      barWidth,
      barRadius,
      maxChars,
      labelEvery,
    };
  }, [width, height, data, seriesCount]);

  return (
    <div className="flex flex-1 flex-col justify-around">
      <div
        ref={wrapRef}
        className="relative h-[250px] w-full min-h-[200px] overflow-hidden"
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            No data for this period yet.
          </div>
        ) : (
          geo && (
            <svg
              width={width}
              height={height}
              viewBox={`0 0 ${width} ${height}`}
              className="block"
            >
              {/* grid + y labels */}
              {geo.ticks.map((t) => (
                <g key={`y-${t}`}>
                  <line
                    x1={geo.padding.left}
                    x2={geo.padding.left + geo.plotW}
                    y1={geo.yAt(t)}
                    y2={geo.yAt(t)}
                    stroke="#f1f5f9"
                    strokeDasharray={t === 0 ? "0" : "4 4"}
                  />
                  <text
                    x={geo.padding.left - 8}
                    y={geo.yAt(t) + 3.5}
                    textAnchor="end"
                    fontSize="10"
                    fill="#9ca3af"
                  >
                    {t}
                  </text>
                </g>
              ))}

              {/* x labels */}
              {data.map((row, i) => {
                if (i % geo.labelEvery !== 0 && i !== data.length - 1) return null;
                const cx = geo.groupCenter(i);
                return (
                  <g key={`x-${i}`} fill="#9ca3af" fontSize="10">
                    <text x={cx} y={geo.baseline + 18} textAnchor="middle">
                      {truncate(row.channelName || "-", geo.maxChars)}
                    </text>
                    <text x={cx} y={geo.baseline + 31} textAnchor="middle" fill="#cbd5e1">
                      {truncate(row.experienceType || "", geo.maxChars)}
                    </text>
                  </g>
                );
              })}

              {/* highlight behind hovered group */}
              {hoveredIdx !== null && (
                <rect
                  x={geo.groupX(hoveredIdx)}
                  y={geo.padding.top}
                  width={geo.groupWidth}
                  height={geo.plotH}
                  fill="#f8fafc"
                  pointerEvents="none"
                />
              )}

              {/* bars */}
              {data.map((row, i) =>
                CHART_SERIES.map((s, si) => {
                  const value = Number(row[s.key]) || 0;
                  const h = geo.barHeight(value);
                  if (h <= 0.5) return null;
                  return (
                    <path
                      key={`${s.key}-${i}`}
                      d={topRoundedRectPath(
                        geo.barX(i, si),
                        geo.baseline - h,
                        geo.barWidth,
                        h,
                        geo.barRadius
                      )}
                      // fill={s.color}
                      // opacity={hoveredIdx === null || hoveredIdx === i ? 1 : 0.4}
                      fill={hoveredIdx === i ? s.hoverColor : s.color}
                      opacity={hoveredIdx === null || hoveredIdx === i ? 1 : 0.4}
                      style={{
                        transition: "fill 180ms ease, opacity 180ms ease",
                      }}
                    />
                  );
                })
              )}

              {/* baseline */}
              <line
                x1={geo.padding.left}
                x2={geo.padding.left + geo.plotW}
                y1={geo.baseline}
                y2={geo.baseline}
                stroke="#e5e7eb"
              />

              {/* hover bands */}
              {data.map((_, i) => (
                <rect
                  key={`band-${i}`}
                  x={geo.groupX(i)}
                  y={0}
                  width={geo.groupWidth}
                  height={geo.padding.top + geo.plotH}
                  fill="transparent"
                  onMouseEnter={() => setHoveredIdx(i)}
                />
              ))}
            </svg>
          )
        )}

        {/* Hover Tooltip - Safely reads counts when hoveredIdx is active */}
        {geo && hoveredIdx !== null && data[hoveredIdx] && (
          <div
            className="pointer-events-none absolute z-20 min-w-[170px] rounded-xl border border-gray-100 bg-white p-3 shadow-xl transition-all duration-150"
            style={{
              left: clamp(geo.groupCenter(hoveredIdx), 95, width - 95),
              top: clamp(geo.padding.top + 10, 10, height - 130),
              transform: "translateX(-50%)",
            }}
          >
            <p className="mb-2.5 text-xs font-bold text-gray-800">
              {data[hoveredIdx].channelName || ""}
              {data[hoveredIdx].experienceType
                ? ` + ${data[hoveredIdx].experienceType}`
                : ""}
            </p>
            <div className="space-y-1.5 text-xs font-medium">
              {CHART_SERIES.map((s) => (
                <div key={s.key} className="flex items-center gap-2" style={{ color: s.color }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  <span>{s.label} : {data[hoveredIdx][s.key] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Static Legend at Bottom */}
      <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-6 gap-y-1.5">
        {CHART_SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-xs font-semibold text-gray-600">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
          </div>
        ))}
      </div>
    </div >
  );
};

const Dashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const firstName = user?.userName?.split(" ")[0] ?? "there";

  const [countsData, setCountsData] = useState(null);
  const [topCompanies, setTopCompanies] = useState([]);
  const [recentAssessments, setRecentAssessments] = useState([]);
  const [hrUsersAdded, setHrUsersAdded] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assessmentStatusBreakdown, setAssessmentStatusBreakdown] = useState(null);
  const [companyStatusBreakdown, setCompanyStatusBreakdown] = useState(null);
  const [clientUserStatusBreakdown, setClientUserStatusBreakdown] = useState(null);
  const [responseBreakdown, setResponseBreakdown] = useState([]);
  const [hoveredStatLabel, setHoveredStatLabel] = useState(null);

  const today = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState(null);
  const yearOptions = useMemo(() => getYearOptions(5), []);
  const isFirstGraphRender = useRef(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await GetDashboardData();
        if (response?.data?.status === "success") {
          const {
            countsData,
            topFiveCompany,
            recentAssessment,
            recentClientUsers,
            assessmentStatusCounts,
            companyClientStatusCounts,
            clientUserStatusCounts,
            responseBreakdown: responseBreakdownData,
            graphData: dynamicGraphData,
          } = response.data.data || {};

          setCountsData(countsData ?? null);
          setTopCompanies(topFiveCompany ?? []);
          setRecentAssessments(recentAssessment ?? []);
          setHrUsersAdded(recentClientUsers ?? []);
          setGraphData(dynamicGraphData ?? []);

          setAssessmentStatusBreakdown(assessmentStatusCounts?.[0] ?? null);
          setCompanyStatusBreakdown(companyClientStatusCounts?.[0] ?? null);
          setClientUserStatusBreakdown(clientUserStatusCounts?.[0] ?? null);
          setResponseBreakdown(responseBreakdownData ?? []);
        } else {
          setError("Failed to load dashboard data.");
        }
      } catch (error) {
        setError("An error occurred while fetching dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (isFirstGraphRender.current) {
      isFirstGraphRender.current = false;
      return;
    }

    const fetchGraphData = async () => {
      try {
        setGraphLoading(true);
        setGraphError(null);
        const response = await GetGraphDataByFilter(selectedMonth, selectedYear);
        if (response?.data?.status === "success") {
          setGraphData(response.data.data ?? []);
        }
      } catch (error) {
      } finally {
        setGraphLoading(false);
      }
    };

    fetchGraphData();
  }, [selectedMonth, selectedYear]);

  const STAT_CARDS = [
    {
      key: "assessments",
      label: "Total Assessments",
      value: countsData?.totalAssessment ?? 0,
      icon: ClipboardList,
      iconBg: "bg-[#c2a8f033]",
      iconColor: "text-[#7f35b2]",
      sparkColor: "#7f35b2",
      sparkline: SPARKLINE_SHAPES.assessments,
      viewAllPath: "/dashboard/Assessment-list",
    },
    {
      key: "companies",
      label: "Enrolled Companies",
      value: countsData?.totalCompanyCount ?? 0,
      icon: Building2,
      iconBg: "bg-[#c2a8f033]",
      iconColor: "text-[#7f35b2]",
      sparkColor: "#7f35b2",
      sparkline: SPARKLINE_SHAPES.companies,
      viewAllPath: "/dashboard/companies",
    },
    {
      key: "hrUsers",
      label: "Enrolled HR Users",
      value: countsData?.totalClientCount ?? 0,
      icon: Users,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-500",
      sparkColor: "#3b82f6",
      sparkline: SPARKLINE_SHAPES.hrUsers,
      viewAllPath: "/dashboard/users",
    },
    {
      key: "responses",
      label: "Total Responses",
      value: countsData?.totalResponderCount ?? 0,
      icon: User,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-500",
      sparkColor: "#f97316",
      sparkline: SPARKLINE_SHAPES.responses,
      viewAllPath: "/dashboard/reports",
    },
  ];

  const statBreakdowns = useMemo(() => {
    const breakdowns = {};

    if (assessmentStatusBreakdown) {
      breakdowns["Total Assessments"] = [
        { label: "Active", value: assessmentStatusBreakdown.activeAssessmentCount ?? 0 },
        { label: "Scheduled", value: assessmentStatusBreakdown.scheduledAssessmentCount ?? 0 },
        { label: "Completed", value: assessmentStatusBreakdown.completedAssessmentCount ?? 0 },
        { label: "Draft", value: assessmentStatusBreakdown.draftAssessmentCount ?? 0 },
        { label: "Inactive", value: assessmentStatusBreakdown.inactiveAssessmentCount ?? 0 },
      ];
    }

    if (companyStatusBreakdown) {
      breakdowns["Enrolled Companies"] = [
        { label: "Active", value: companyStatusBreakdown.activeCompanyCount ?? 0 },
        { label: "Inactive", value: companyStatusBreakdown.inactiveCompanyCount ?? 0 },
      ];
    }

    if (clientUserStatusBreakdown) {
      breakdowns["Enrolled HR Users"] = [
        { label: "Active", value: clientUserStatusBreakdown.activeClientUserCount ?? 0 },
        { label: "Inactive", value: clientUserStatusBreakdown.inactiveClientUserCount ?? 0 },
      ];
    }

    if (responseBreakdown.length > 0) {
      const byKey = {};
      responseBreakdown.forEach((entry) => {
        const title = entry.channelName || "Other";
        const subtitle = entry.experienceType || "Other";
        const key = `${title}__${subtitle}`;
        if (!byKey[key]) byKey[key] = { title, subtitle, value: 0 };
        byKey[key].value += entry.totalResponsesCount ?? 0;
      });
      breakdowns["Total Responses"] = Object.values(byKey).map(({ title, subtitle, value }) => ({
        label: { title, subtitle },
        value,
      }));
    }

    return breakdowns;
  }, [assessmentStatusBreakdown, companyStatusBreakdown, clientUserStatusBreakdown, responseBreakdown]);

  return (
    <div className="flex flex-col gap-4 lg:h-full lg:gap-3">
      {hoveredStatLabel && (
        <div className="fixed inset-0 z-40 bg-white/30 backdrop-blur-sm pointer-events-none" />
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 lg:shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
            Welcome, {firstName} 👋
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Here's what's happening with your assessments today.
          </p>
        </div>
      </div>

      <div className="relative grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:shrink-0 lg:grid-cols-4 lg:gap-3">
        {STAT_CARDS.map((card) => (
          <StatCard
            key={card.key}
            card={card}
            rows={statBreakdowns[card.label] || []}
            isHovered={hoveredStatLabel === card.label}
            onMouseEnter={() => setHoveredStatLabel(card.label)}
            onMouseLeave={() => setHoveredStatLabel(null)}
            navigate={navigate}
          />
        ))}
      </div>

      <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:gap-3">
        {/* Equal-height parent row grid */}
        <div className="grid grid-cols-1 gap-4 lg:min-h-0 lg:flex-[3] lg:grid-cols-3 lg:gap-3">

          {/* Assessment overview */}
          <div className="flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="mb-3 flex shrink-0 items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold uppercase tracking-wider text-[#7f35b2]">
                  Performance
                </span>
                <h2 className="text-lg font-bold text-gray-900">
                  Assessment overview by channel
                </h2>
              </div>

              <MonthYearFilter
                month={selectedMonth}
                year={selectedYear}
                years={yearOptions}
                onMonthChange={setSelectedMonth}
                onYearChange={setSelectedYear}
                disabled={graphLoading}
              />
            </div>

            {graphError ? (
              <div className="flex h-[250px] items-center justify-center text-sm text-red-500">
                {graphError}
              </div>
            ) : (
              <div className="relative flex flex-1 flex-col">
                {graphLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <span className="text-xs font-medium text-gray-400">Loading…</span>
                  </div>
                )}
                <OverviewChart rawData={graphData} />
              </div>
            )}
          </div>

          {/* Top 5 Companies */}
          <div className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Top 5 companies</h2>
              <button
                className="rounded-md bg-purple-50 px-3 py-1 text-xs font-semibold text-[#7f35b2] transition-colors hover:bg-purple-100"
                onClick={() => navigate("/dashboard/companies")}
              >
                View all →
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto pr-1">
              <table className="w-full min-w-[580px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-400">
                    <th className="pb-3 pr-4 font-semibold">Company</th>
                    <th className="pb-3 pr-4 font-semibold">Assessments</th>
                    <th className="pb-3 pr-4 font-semibold">Responses</th>
                    <th className="pb-3 pr-4 font-semibold">Submitted</th>
                    <th className="pb-3 font-semibold">Contact Person</th>
                  </tr>
                </thead>

                <tbody>
                  {topCompanies.map((c, idx) => (
                    <tr
                      key={c.companyName || idx}
                      className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                    >
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-[#7f35b2]">
                            <Building2 size={16} />
                          </span>

                          <div className="min-w-0">
                            <p className="truncate font-semibold text-gray-900">
                              {c.companyName || "-"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(c.companyCreatedOn)}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="py-3 pr-4 font-medium text-gray-600">
                        {c.totalAssessmentsAssigned ?? 0}
                      </td>

                      <td className="py-3 pr-4 font-medium text-gray-600">
                        {c.totalRespondersCreated ?? 0}
                      </td>

                      <td className="py-3 pr-4 font-medium text-gray-600">
                        {c.completedRespondersCount ?? 0}
                      </td>

                      <td className="py-3">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-gray-900">
                            {c.contactPersonName || "-"}
                          </p>
                          <p className="truncate text-xs text-gray-500">
                            {c.contactMail || "-"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:min-h-0 lg:flex-[2] lg:grid-cols-3 lg:gap-3">
          {/* Recent assessments */}
          <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">
                Recent assessments
              </h2>
              <button
                className="rounded-md bg-purple-50 px-3 py-1 text-xs font-semibold text-[#7f35b2] transition-colors hover:bg-purple-100"
                onClick={() => navigate("/dashboard/Assessment-list")}
              >
                View all →
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto pr-1">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-400">
                    <th className="pb-3 pr-4 font-semibold">Assessment Name</th>
                    <th className="pb-3 pr-4 font-semibold">Owner</th>
                    <th className="pb-3 pr-4 font-semibold">Channel Name</th>
                    <th className="pb-3 pr-4 font-semibold">Experience Type</th>
                    <th className="pb-3 pr-4 font-semibold">Responders</th>
                    <th className="pb-3 font-semibold">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAssessments.map((row) => (
                    <tr key={row.uniqueKey} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                      <td className="py-3 pr-4">
                        <div className="min-w-0 max-w-[220px]">
                          <p className="truncate font-semibold text-gray-900" title={row.assessmentName}>
                            {row.assessmentName || "-"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(row.createdDate)}
                          </p>
                        </div>
                      </td>

                      <td className="py-3 pr-4">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-gray-900">
                            {row.ownerName || "-"}
                          </p>
                          <p className="truncate text-xs text-gray-500">
                            {row.ownerEmail || "-"}
                          </p>
                        </div>
                      </td>

                      <td className="py-3 pr-4 font-medium text-gray-600">
                        {row.channelName || "-"}
                      </td>

                      <td className="py-3 pr-4 font-medium text-gray-600">
                        {row.experienceType || "-"}
                      </td>

                      <td className="py-3 pr-4 font-medium text-gray-600">
                        {row.totalResponderCount ?? 0}
                      </td>

                      <td className="py-3 font-medium text-gray-600">
                        {row.completedResponderCount ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* HR users added */}
          <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">
                HR users added
              </h2>
              <button
                className="rounded-md bg-purple-50 px-3 py-1 text-xs font-semibold text-[#7f35b2] transition-colors hover:bg-purple-100"
                onClick={() => navigate("/dashboard/users")}
              >View all →</button>
            </div>
            <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto pr-1">
              <table className="w-full min-w-[600px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-400">
                    <th className="pb-3 pr-4 font-semibold">HR User</th>
                    <th className="pb-3 pr-4 font-semibold">Company</th>
                    <th className="pb-3 pr-4 font-semibold">Invited</th>
                    <th className="pb-3 pr-4 font-semibold">Completed</th>
                    <th className="pb-3 font-semibold">Joined On</th>
                  </tr>
                </thead>
                <tbody>
                  {hrUsersAdded.slice(0, 5).map((u, idx) => (
                    <tr key={u.email || idx} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-50 text-xs font-bold text-[#7f35b2]">
                            {initialsOf(u.hrUserName)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-gray-900">
                              {u.hrUserName || "-"}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                              {u.email || "-"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 font-medium text-gray-600">{u.companyName || "-"}</td>
                      <td className="py-3 pr-4 font-medium text-gray-600">{u.totalInvitedCount ?? 0}</td>
                      <td className="py-3 pr-4 font-medium text-gray-600">{u.completedResponderCount ?? 0}</td>
                      <td className="py-3 font-medium text-gray-500">{formatDate(u.createdOn)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;